// 017 — Protocolo Quirúrgico (Anexo 1 MSP, SNS-MSP/HCU-form. 017/2021). Bloques A-J del papel (páginas 77-78 del PDF);
// datos y decisiones en `bloques017Datos.ts`. Al guardar deja, en UNA transacción, el `Procedure` del "Realizado" y una
// `Condition` por diagnóstico pre y postoperatorio; el resto va como `Par[]` en el PDF firmado.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { Bloque, CamposSimples } from './camposSimples';
import { codeDeDiagnostico, SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_ANESTESIA_017,
  CAMPOS_COMPLICACIONES_017,
  CAMPOS_EQUIPO_017,
  CAMPOS_HISTOPATOLOGICOS_017,
  CAMPOS_PROCEDIMIENTO_017,
  CAMPOS_TIEMPOS_017,
  COLUMNAS_PROFESIONALES_017,
  FILAS_PROFESIONALES_017,
  construirProcedure017,
  diagnosticosConTexto017,
  estadoInicial017,
  paresDe017,
  type Estado017,
} from './bloques017Datos';

const CODIGO = '017';

type ClaveValores = 'procedimiento' | 'equipo' | 'anestesia' | 'tiempos' | 'complicaciones' | 'histopatologicos';

export function Formulario017({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f017.titulo');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Protocolo quirúrgico: el papel no trae servicio; es de quirófano.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: 'Quirófano' });

  const [estado, setEstado] = useState<Estado017>(() => estadoInicial017());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahora = new Date();
      const lote = new LoteTransaccional();
      const procedure = construirProcedure017(estado, subject, encounter, ahora);
      if (procedure) lote.crear(procedure);
      for (const d of diagnosticosConTexto017(estado)) {
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d), recordedDate: ahora.toISOString() });
      }
      await lote.enviar(medplum);
      onGuardado(paresDe017(t, estado), datosEncabezadoMsp);
    });

  function bloque(clave: ClaveValores, tituloClave: string, prefijo: string, lista: Parameters<typeof CamposSimples>[0]['campos'], columnas?: 2 | 3 | 4): JSX.Element {
    return (
      <Bloque titulo={t(`formularios.f017.${tituloClave}`)}>
        <CamposSimples prefijoId={`f017-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} onChange={(v) => setEstado((prev) => ({ ...prev, [clave]: v }))} />
      </Bloque>
    );
  }

  const diagnosticos = (clave: 'diagnosticosPre' | 'diagnosticosPost', rotulo: string): JSX.Element => (
    <div className="flex flex-col gap-2">
      {estado[clave].map((d, i) => (
        <SelectorDiagnosticoCie10
          key={i}
          label={`${t(`formularios.f017.${rotulo}`)} ${i + 1}`}
          value={d}
          onChange={(v) => setEstado((prev) => ({ ...prev, [clave]: prev[clave].map((x, j) => (j === i ? v : x)) }))}
        />
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f017.diagnosticosTitulo')}>
        {diagnosticos('diagnosticosPre', 'preoperatorio')}
        {diagnosticos('diagnosticosPost', 'postoperatorio')}
      </Bloque>

      {bloque('procedimiento', 'procedimientoTitulo', 'proc', CAMPOS_PROCEDIMIENTO_017)}
      {bloque('equipo', 'equipoTitulo', 'eq', CAMPOS_EQUIPO_017, 2)}
      {bloque('anestesia', 'anestesiaTitulo', 'anest', CAMPOS_ANESTESIA_017, 4)}
      {bloque('tiempos', 'tiemposTitulo', 'tiempos', CAMPOS_TIEMPOS_017)}
      {bloque('complicaciones', 'complicacionesTitulo', 'comp', CAMPOS_COMPLICACIONES_017)}
      {bloque('histopatologicos', 'histopatologicosTitulo', 'histo', CAMPOS_HISTOPATOLOGICOS_017, 2)}

      <Bloque titulo={t('formularios.f017.diagramaTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f017.diagramaNota')}</p>
      </Bloque>

      <Bloque titulo={t('formularios.f017.profesionalesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f017.profesionalesNota')}</p>
        <TablaConColumnas filas={FILAS_PROFESIONALES_017} columnas={COLUMNAS_PROFESIONALES_017} estado={estado.profesionales} onChange={(p) => setEstado((prev) => ({ ...prev, profesionales: p }))} />
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
